import { useRef, useState } from 'react';
import type { ResetAnalysesResult } from '@ozon-ai/analysis/contracts';
import type { ImportFboStockDaysResult, ResetFboStockHistoryResult } from '@ozon-ai/ozon/contracts';
import { Button } from '@/components/ui/button.tsx';
import { api } from '../../../api/client';
import { useTelegram } from '../../../context/TelegramContext';
import { SETTINGS_DEBUG_HUB } from '../settings-paths';
import {
  SettingsErrorAlert,
  SettingsFormCard,
  SettingsHint,
  SettingsSubPageShell,
} from '../settings-shared';

export function SettingsDebugManualPage() {
  const { webApp } = useTelegram();
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<'import' | 'reset' | 'analyses' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [imported, setImported] = useState<ImportFboStockDaysResult | null>(null);
  const [reset, setReset] = useState<ResetFboStockHistoryResult | null>(null);
  const [analyses, setAnalyses] = useState<ResetAnalysesResult | null>(null);

  async function handleFile(file: File) {
    setBusy('import');
    setError(null);
    setImported(null);
    setReset(null);
    setAnalyses(null);
    try {
      const csv = await file.text();
      const result = await api.postText<ImportFboStockDaysResult>(
        '/debug/stocks/import',
        csv,
        'text/csv',
      );
      setImported(result);
      webApp?.HapticFeedback?.notificationOccurred('success');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось загрузить CSV');
      webApp?.HapticFeedback?.notificationOccurred('error');
    } finally {
      setBusy(null);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function handleReset() {
    const confirmed = window.confirm('Удалить все сохранённые дни остатков?');
    if (!confirmed) return;
    setBusy('reset');
    setError(null);
    setImported(null);
    setAnalyses(null);
    try {
      const result = await api.post<ResetFboStockHistoryResult>('/debug/stocks/reset', {});
      setReset(result);
      webApp?.HapticFeedback?.notificationOccurred('success');
    } catch (err) {
      setReset(null);
      setError(err instanceof Error ? err.message : 'Не удалось сбросить историю');
      webApp?.HapticFeedback?.notificationOccurred('error');
    } finally {
      setBusy(null);
    }
  }

  async function handleDeleteAnalyses() {
    const confirmed = window.confirm(
      'Удалить все анализы SKU и дайджесты? Закрепления товаров останутся.',
    );
    if (!confirmed) return;
    setBusy('analyses');
    setError(null);
    setImported(null);
    setReset(null);
    try {
      const result = await api.post<ResetAnalysesResult>('/debug/analyses/reset', {});
      setAnalyses(result);
      webApp?.HapticFeedback?.notificationOccurred('success');
    } catch (err) {
      setAnalyses(null);
      setError(err instanceof Error ? err.message : 'Не удалось удалить анализы');
    } finally {
      setBusy(null);
    }
  }

  return (
    <SettingsSubPageShell title="Ручные методы" backTo={SETTINGS_DEBUG_HUB}>
      <SettingsHint>
        CSV с колонками day, offerId, fboAvailable записывает дни остатков. Повтор той же пары день и
        артикул обновляет количество. Сброс удаляет все сохранённые дни. Ночной снимок запишет текущий
        день заново.
      </SettingsHint>
      <SettingsFormCard>
        <div className="flex flex-col gap-3">
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void handleFile(file);
            }}
          />
          <Button type="button" disabled={busy != null} onClick={() => fileRef.current?.click()}>
            {busy === 'import' ? 'Загружаем…' : 'Загрузить CSV остатков'}
          </Button>
          <Button type="button" variant="destructive" disabled={busy != null} onClick={() => void handleReset()}>
            {busy === 'reset' ? 'Удаляем…' : 'Сброс истории остатков'}
          </Button>
        </div>
      </SettingsFormCard>
      <SettingsFormCard>
        <div className="flex flex-col gap-3">
          <SettingsHint>
            Удаляет сохранённые анализы SKU и дайджесты. Закреплённые товары остаются. Если расчёт ещё
            идёт, удаление будет отклонено.
          </SettingsHint>
          <Button type="button" variant="destructive" disabled={busy != null} onClick={() => void handleDeleteAnalyses()}>
            {busy === 'analyses' ? 'Удаляем…' : 'Удалить все анализы'}
          </Button>
        </div>
      </SettingsFormCard>
      {error && <SettingsErrorAlert>{error}</SettingsErrorAlert>}
      {imported && (
        <SettingsFormCard>
          <p className="text-sm text-muted-foreground">Записано строк: {imported.upsertedCount}</p>
        </SettingsFormCard>
      )}
      {reset && (
        <SettingsFormCard>
          <p className="text-sm text-muted-foreground">Удалено строк: {reset.deletedCount}</p>
        </SettingsFormCard>
      )}
      {analyses && (
        <SettingsFormCard>
          <p className="text-sm text-muted-foreground">
            Анализы SKU: {analyses.productRunsDeleted}. Дайджесты: {analyses.digestRunsDeleted}.
          </p>
        </SettingsFormCard>
      )}
    </SettingsSubPageShell>
  );
}
